<script lang="ts">
  import { router, page } from '@inertiajs/svelte';
  import { openSignOutModal } from '../lib/stores/ui';

  let auth = $derived(
    $page.props.auth as { user: { id: number; email_address: string } | null } | undefined,
  );
  let currentUser = $derived(auth?.user);

  // Keyboard + click accessible open/close state (hover still works via CSS)
  let isMenuOpen = $state(false);

  function toggleMenu() {
    isMenuOpen = !isMenuOpen;
  }

  function closeMenu() {
    isMenuOpen = false;
  }

  function navigate(path: string) {
    closeMenu();
    router.get(path);
  }

  function handleSignOut() {
    closeMenu();
    openSignOutModal();
  }

  function handleWindowKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && isMenuOpen) {
      closeMenu();
      document.querySelector<HTMLElement>('.profile-dropdown-container .profile-btn')?.focus();
    }
  }

  function handleBlur(e: FocusEvent) {
    const wrapper = e.currentTarget as HTMLElement;
    if (!wrapper.contains(e.relatedTarget as Node)) {
      closeMenu();
    }
  }

  function handleClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.profile-dropdown-container')) {
      closeMenu();
    }
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- document-level click listener with conditional setup/teardown and cleanup
  $effect(() => {
    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside, true);
      return () => document.removeEventListener('click', handleClickOutside, true);
    }
  });
</script>

<svelte:window onkeydown={handleWindowKeydown} />

{#if currentUser}
  <div class="divider"></div>
  <div class="profile-dropdown-container hover-dropdown" onfocusout={handleBlur}>
    <button
      type="button"
      class="profile-btn"
      title={currentUser.email_address}
      onclick={toggleMenu}
      aria-expanded={isMenuOpen}
      aria-haspopup="true"
    >
      <svg class="profile-icon" viewBox="0 0 24 24" fill="currentColor">
        <path
          d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
        />
      </svg>
    </button>

    <div class="profile-dropdown" class:open={isMenuOpen}>
      <div class="profile-dropdown-header">
        <span class="profile-dropdown-email">{currentUser.email_address}</span>
      </div>
      <div class="profile-dropdown-divider"></div>
      <button type="button" class="profile-dropdown-item" onclick={() => navigate('/settings')}>
        <svg class="profile-dropdown-icon" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1115.6 12 3.611 3.611 0 0112 15.6z"
          />
        </svg>
        Settings
      </button>
      <button type="button" class="profile-dropdown-item" onclick={() => navigate('/trash')}>
        <svg
          class="profile-dropdown-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
        </svg>
        Trash
      </button>
      <button type="button" class="profile-dropdown-item" onclick={() => navigate('/changelog')}>
        <svg class="profile-dropdown-icon" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"
          />
        </svg>
        What's New
      </button>
      <button type="button" class="profile-dropdown-item" onclick={handleSignOut}>
        <svg class="profile-dropdown-icon" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"
          />
        </svg>
        Sign out
      </button>
    </div>
  </div>
{/if}

<style>
  .divider {
    width: 1px;
    height: 1.25rem;
    background: #e5e7eb;
  }

  .profile-dropdown-container {
    position: relative;
  }

  .profile-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    color: #6b7280;
    background: #f3f4f6;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.15s;
  }

  .profile-btn:hover {
    color: #374151;
    background: #e5e7eb;
  }

  .profile-icon {
    width: 1.125rem;
    height: 1.125rem;
  }

  .profile-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.375rem;
    min-width: 12rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 50;
    overflow: visible;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
    transition-delay: 0.15s;
  }

  /* Invisible bridge between trigger and dropdown to prevent hover gap */
  .profile-dropdown::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
  }

  .hover-dropdown:hover > .profile-dropdown,
  .profile-dropdown.open {
    opacity: 1;
    visibility: visible;
    transition-delay: 0s;
  }

  .profile-dropdown-header {
    padding: 0.5rem 0.75rem;
  }

  .profile-dropdown-email {
    font-size: 0.75rem;
    color: #374151;
    font-weight: 500;
    word-break: break-all;
  }

  .profile-dropdown-divider {
    height: 1px;
    background: #e5e7eb;
  }

  .profile-dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    color: #374151;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
  }

  .profile-dropdown-item:hover {
    background: #f3f4f6;
  }

  .profile-dropdown-icon {
    width: 1rem;
    height: 1rem;
    color: #6b7280;
  }
</style>
